import React, { useState, useEffect } from 'react';
import {
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    TextField,
    InputAdornment,
    Typography,
    Chip,
    Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import Main from '../layouts/Main';
import EditSeoDialog from '../components/EditSeoDialog';

// Hər page_key üçün mövcud placeholder-lər
const PAGE_PLACEHOLDERS = {
    'product_detail': [
        { key: '{{name}}', desc: 'Product name' },
        { key: '{{sku}}', desc: 'Product SKU code' },
        { key: '{{brand}}', desc: 'Product brand' },
        { key: '{{category}}', desc: 'Product category' },
        { key: '{{price}}', desc: 'Product price' },
    ],
    'catalog_category': [
        { key: '{{category}}', desc: 'Selected category' },
        { key: '{{page}}', desc: 'Page number' },
    ],
    'catalog_brand': [
        { key: '{{brand}}', desc: 'Selected brand' },
        { key: '{{page}}', desc: 'Page number' },
    ],
    'catalog_combined': [
        { key: '{{category}}', desc: 'Selected category' },
        { key: '{{brand}}', desc: 'Selected brand' },
        { key: '{{page}}', desc: 'Page number' },
    ],
};

const PAGE_LABELS = {
    'home': 'Home Page',
    'contact': 'Contact',
    'privacy': 'Privacy Policy',
    'terms': 'Terms & Conditions',
    'cookie': 'Cookie Policy',
    'catalog_root': 'Catalog (Root)',
    'catalog_category': 'Catalog (Category)',
    'catalog_brand': 'Catalog (Brand)',
    'catalog_combined': 'Catalog (Category+Brand)',
    'product_detail': 'Product Detail',
};

const LANG_LABELS = {
    'en': 'EN',
    'fi': 'FI',
    'de': 'DE',
    'fr': 'FR',
};

const SeoSettings = () => {
    const [seoSettings, setSeoSettings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedSetting, setSelectedSetting] = useState(null);

    const fetchSeoSettings = async () => {
        try {
            setLoading(true);
            const response = await fetch('https://api.elnaf.fi/api/get_all_seo.php');
            const data = await response.json();
            setSeoSettings(data || []);
        } catch (error) {
            console.error('Error loading SEO settings:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSeoSettings();
    }, []);

    const handleEditClick = (setting) => {
        setSelectedSetting(setting);
        setEditDialogOpen(true);
    };

    const handleEditClose = () => {
        setEditDialogOpen(false);
        setSelectedSetting(null);
    };

    const handleEditSave = async () => {
        setEditDialogOpen(false);
        setSelectedSetting(null);
        await fetchSeoSettings();
    };

    // Axtarış filtri
    const filteredSettings = seoSettings.filter(item => {
        const search = searchTerm.toLowerCase();
        return (
            (item.page_key || '').toLowerCase().includes(search) ||
            (item.meta_title || '').toLowerCase().includes(search) ||
            (item.meta_description || '').toLowerCase().includes(search) ||
            (item.lang || '').toLowerCase().includes(search)
        );
    });

    // page_key ilə qruplaşdırma
    const grouped = {};
    filteredSettings.forEach(item => {
        if (!grouped[item.page_key]) {
            grouped[item.page_key] = [];
        }
        grouped[item.page_key].push(item);
    });

    return (
        <Main>
            <div className="page-header">
                <div>
                    <h1 className="page-title">SEO Settings</h1>
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item">Home</li>
                        <li className="breadcrumb-item active" aria-current="page">SEO Settings</li>
                    </ol>
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="card custom-card">
                        <div className="card-header">
                            <div className="card-title">SEO Settings Management</div>
                        </div>
                        <div className="card-body">
                            <Box sx={{ mb: 3 }}>
                                <TextField
                                    fullWidth
                                    placeholder="Search SEO settings..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>

                            {/* Placeholder Guide */}
                            <Box sx={{ mb: 3, p: 2, bgcolor: '#f0f7ff', borderRadius: 2, border: '1px solid #c8dff8' }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, color: '#1565c0', fontWeight: 600 }}>
                                    ℹ️ Dynamic Placeholders
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                    Some pages support dynamic values in SEO fields.
                                    For example, <code>{'{{name}}'}</code> will be replaced with the actual product name.
                                    Available placeholders for each page are shown in the edit dialog.
                                </Typography>
                            </Box>

                            {loading ? (
                                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <TableContainer component={Paper}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell><strong>Page</strong></TableCell>
                                                <TableCell><strong>Lang</strong></TableCell>
                                                <TableCell><strong>Meta Title</strong></TableCell>
                                                <TableCell><strong>Meta Description</strong></TableCell>
                                                <TableCell><strong>Placeholders</strong></TableCell>
                                                <TableCell align="center"><strong>Actions</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {Object.keys(grouped).map(pageKey => (
                                                grouped[pageKey].map((item, idx) => (
                                                    <TableRow key={item.id} hover sx={idx === 0 ? { borderTop: '2px solid #e0e0e0' } : {}}>
                                                        {idx === 0 && (
                                                            <TableCell rowSpan={grouped[pageKey].length}>
                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                    {PAGE_LABELS[pageKey] || pageKey}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                                                    {pageKey}
                                                                </Typography>
                                                            </TableCell>
                                                        )}
                                                        <TableCell>
                                                            <Chip
                                                                label={LANG_LABELS[item.lang] || item.lang}
                                                                size="small"
                                                                color="primary"
                                                                variant="outlined"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {item.meta_title || '—'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {item.meta_description || '—'}
                                                            </Typography>
                                                        </TableCell>
                                                        {idx === 0 && (
                                                            <TableCell rowSpan={grouped[pageKey].length}>
                                                                {PAGE_PLACEHOLDERS[pageKey] ? (
                                                                    <Box display="flex" gap={0.5} flexWrap="wrap">
                                                                        {PAGE_PLACEHOLDERS[pageKey].map(ph => (
                                                                            <Tooltip key={ph.key} title={ph.desc} arrow>
                                                                                <Chip
                                                                                    label={ph.key}
                                                                                    size="small"
                                                                                    sx={{ fontFamily: 'monospace', fontSize: '0.7rem', bgcolor: '#e8f5e9', color: '#2e7d32' }}
                                                                                />
                                                                            </Tooltip>
                                                                        ))}
                                                                    </Box>
                                                                ) : (
                                                                    <Typography variant="caption" color="text.secondary">—</Typography>
                                                                )}
                                                            </TableCell>
                                                        )}
                                                        <TableCell align="center">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleEditClick(item)}
                                                                title="Edit SEO"
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}

                            {!loading && filteredSettings.length === 0 && (
                                <Box textAlign="center" py={5}>
                                    <Typography variant="h6" color="text.secondary">
                                        {searchTerm ? 'No results found.' : 'No SEO settings found.'}
                                    </Typography>
                                </Box>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <EditSeoDialog
                open={editDialogOpen}
                onClose={handleEditClose}
                setting={selectedSetting}
                onSave={handleEditSave}
                placeholders={selectedSetting ? PAGE_PLACEHOLDERS[selectedSetting.page_key] : null}
                pageLabel={selectedSetting ? (PAGE_LABELS[selectedSetting.page_key] || selectedSetting.page_key) : ''}
            />
        </Main>
    );
};

export default SeoSettings;
